import { useState } from 'react';
import { Box } from '@mui/material';
import {
  Breadcrumb,
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  FormSearchResult,
  useDataProvider,
  useLocalization,
} from '@ui-framework/index';
import { useNavigate } from 'react-router-dom';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { observer } from 'mobx-react';

import {
  IconLibrary,
  Layout,
  StackablePageButtons,
  FormFields,
  PageSection,
  getDefaultValueByControl,
} from '@product-framework/index';

import { warrantyType, warrantyTypeCode, status } from './accessorFunctions';
import { listSchema, searchSchema } from '../../schemas';
import validationSchema from './validations';

function ServiceAdministratorWarrantyTypeList() {
  const { formatMessage } = useLocalization();
  const navigate = useNavigate();
  const [showSearch, setShowSearch] = useState(false);
  const [resultsFound, setResultsFound] = useState(0);

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const dataProvider = useDataProvider(provider);

  const listApiUrl = `${dataProvider.getApiUrl()}/sxcl/v1/service-administrator-warranty-types`;

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'LIST_SERVICE_ADMINISTRATOR_WARRANTY_TYPES',
        defaultMessage: 'List Service Administrator Warranty Types',
      }),
      icon: IconLibrary.ServiceAdministratorWarrantyTypes,
      onClick: undefined,
    },
  ];

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'CREATE_SERVICE_ADMINISTRATOR_WARRANTY_TYPES',
        defaultMessage: 'Create Service Administrator Warranty Types',
      }),
      Icon: IconLibrary.ServiceAdministratorWarrantyType,
      variant: 'outlined',
      onClick: () => {
        navigate('/serviceadministratorwarrantytypes/create');
      },
      permissionName: 'CAN_CREATE_EDIT_MAINTENANCE_PAGES',
    },
    {
      label: !showSearch
        ? formatMessage({
            id: 'SHOW_SEARCH',
            defaultMessage: 'Show Search',
          })
        : formatMessage({
            id: 'HIDE_SEARCH',
            defaultMessage: 'Hide Search',
          }),
      Icon: !showSearch ? IconLibrary.Search : IconLibrary.HideSearch,
      variant: 'contained',
      onClick: () => {
        setShowSearch(!showSearch);
      },
    },
  ];

  const preProcessSearchRequestData = data => {
    const { id: idArg, ...other } = data;
    const searchParams = Object.fromEntries(
      Object.entries({
        ...other,
      }).filter(
        ([, value]) => value !== undefined && value !== null && value !== '',
      ),
    );
    const urlParams = JSON.stringify(searchParams);
    return urlParams;
  };

  const submitSearchForm = async data => {
    const requestData = preProcessSearchRequestData(data);
    navigate(
      `/serviceadministratorwarrantytypes?filters=${requestData}&amp;page=0`,
    );
  };

  const preProcessSearchValues = searchedParams => {
    const { ...other } = searchedParams;
    return {
      ...other,
    };
  };

  const listProps = {
    columnPrimaryId: 'id',
    storeName: 'ServiceAdministratorWarrantyTypeList',
    dataProvider,
    schema: listSchema,
    initialState: {
      showColumnFilters: false,
    },
    downloadApiUrl: null,
    listApiUrl,
    accessorFunctions: {
      warrantyType,
      warrantyTypeCode,
      status,
    },
    onRowCountChange: rowCount => {
      setResultsFound(rowCount);
    },
    sortOrder: 'ASC',
    sortBy: 'warrantyType',
  };

  const searchProps = {
    customComponents: FormFields,
    IconLibrary,
    schema: searchSchema,
    onSubmit: submitSearchForm,
    onClear: () => {
      navigate(`/serviceadministratorwarrantytypes`);
    },
    validationSchema: validationSchema(formatMessage),
    hideSearchForm: () => {
      setShowSearch(false);
    },
    getValueByControl: getDefaultValueByControl,
  };

  const searchValueFormatter = ({ name: searchName, value }) => {
    switch (searchName) {
      case 'status':
        return value === 'true' ? 'Active' : 'In-Active';

      default:
        return value;
    }
  };

  const searchParamsProps = {
    preProcessSearchValues,
    schema: searchSchema,
    resultsFound,
    valueFormatter: searchValueFormatter,
  };

  return (
    <Layout>
      <Grid2 xs={12} container>
        <Grid2 xs={12} md={6}>
          <Breadcrumb items={breadCrumb} />
        </Grid2>
        <Grid2 xs={12} md={6}>
          <StackablePageButtons buttons={stackablePageButtons} />
        </Grid2>
      </Grid2>
      <Grid2 xs={12}>
        <FormSearchResult {...searchParamsProps} />
      </Grid2>

      {showSearch && (
        <PageSection
          containerProps={{ sx: { mb: 3 } }}
          label={formatMessage({
            id: 'SEARCH_SERVICE_ADMINISTRATOR_WARRANTY_TYPES',
            defaultMessage: 'Search Service Administrator Warranty Types',
          })}>
          <Box>
            <CRUDComponent
              type={CRUD_COMPONENT_TYPE.SEARCH}
              searchProps={searchProps}
            />
          </Box>
        </PageSection>
      )}

      <PageSection
        label={formatMessage({
          id: 'SERVICE_ADMINISTRATOR_WARRANTY_TYPES',
          defaultMessage: 'Service Administrator Warranty Types',
        })}>
        <Box>
          <CRUDComponent
            type={CRUD_COMPONENT_TYPE.LIST}
            listProps={listProps}
          />
        </Box>
      </PageSection>
    </Layout>
  );
}

export default observer(ServiceAdministratorWarrantyTypeList);
